// Provider based on react-admin simple rest provider
import { stringify } from 'query-string';
import {
    fetchUtils,
    GET_LIST,
    GET_ONE,
    GET_MANY,
    GET_MANY_REFERENCE,
    CREATE,
    UPDATE,
    UPDATE_MANY,
    DELETE,
    DELETE_MANY,
} from 'react-admin';

/**
 * Maps react-admin queries to a simple REST API
 *
 * The REST dialect is similar to the one of FakeRest
 * @see https://github.com/marmelab/FakeRest
 * @example
 * GET_LIST     => GET http://my.api.url/posts?sort=['title','ASC']&range=[0, 24]
 * GET_ONE      => GET http://my.api.url/posts/123
 * GET_MANY     => GET http://my.api.url/posts?filter={ids:[123,456,789]}
 * UPDATE       => PUT http://my.api.url/posts/123
 * CREATE       => POST http://my.api.url/posts/123
 * DELETE       => DELETE http://my.api.url/posts/123
 */

export default (apiUrl, httpClient = fetchUtils.fetchJson) => {
    /**
     * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
     * @param {String} resource Name of the resource to fetch, e.g. 'posts'
     * @param {Object} params The data request params, depending on the type
     * @returns {Object} { url, options } The HTTP request parameters
     */
    const convertDataRequestToHTTP = (type, resource, params) => {
        let url = '';
        const options = {};
        switch (type) {
            case GET_LIST: {
                const { page, perPage } = params.pagination;
                const { field, order } = params.sort;
                const query = {
                    sort: JSON.stringify({[field]: order}),
                    range: JSON.stringify([
                        (page - 1) * perPage,
                        page * perPage - 1,
                    ]),
                    filter: JSON.stringify(params.filter),
                };
                url = `${apiUrl}/${resource}?${stringify(query)}`;
                break;
            }
            case GET_ONE:
                if (resource === 'phases') {
                    url = `${apiUrl}/${resource}`;
                } else {
                    url = `${apiUrl}/${resource}/${params.id}`;
                }
                break;
            case GET_MANY: {
                const query = {
                    filter: JSON.stringify({ id: params.ids }),
                };
                url = `${apiUrl}/${resource}?${stringify(query)}`;
                break;
            }
            case GET_MANY_REFERENCE: {
                const { page, perPage } = params.pagination;
                const { field, order } = params.sort;
                const query = {
                    sort: JSON.stringify([field, order]),
                    range: JSON.stringify([
                        (page - 1) * perPage,
                        page * perPage - 1,
                    ]),
                    filter: JSON.stringify({
                        ...params.filter,
                        [params.target]: params.id,
                    }),
                };
                url = `${apiUrl}/${resource}?${stringify(query)}`;
                break;
            }

            // Transform data depending on endpoint before sending a HTTP request
            case UPDATE:
                url = `${apiUrl}/${resource}/${params.id}`;
                options.method = 'PATCH';
                if (resource === 'admin/projects') {
                    options.body = JSON.stringify({
                        "contestantName": params.data.contestantName,
                        "projectTitle": params.data.projectTitle,
                        "status": params.data.status,
                        "projectCategory": params.data.projectCategory,
                        "contestantAnswer1": params.data.contestantAnswer1,
                        "contestantAnswer2": params.data.contestantAnswer2,
                        "contestantAnswer3": params.data.contestantAnswer3,
                        "contestantStreet": params.data.contestantStreet,
                        "contestantZipcode": params.data.contestantZipcode,
                        "contestantCity": params.data.contestantCity,
                        "_id": params.data._id,
                    });
                } else if(resource === 'phases') {
                    url = `${apiUrl}/${resource}`;
                    options.body = JSON.stringify({
                        "campaignLaunch": params.data.campaignLaunch,
                        "voteStart": params.data.voteStart,
                        "voteStartFinale": params.data.voteStartFinale,
                        "voteEnd": params.data.voteEnd,
                    });
                } else if(resource === 'users') {
                    options.body = JSON.stringify({
                        "role": params.data.role,
                        "salutation": params.data.salutation,
                        "firstname": params.data.firstname,
                        "lastname": params.data.lastname,
                        "email": params.data.email,
                        "userId": params.data._id,
                    });
                } else {
                    options.body = JSON.stringify(params.data);
                }
                break;
            case CREATE:
                const userId = localStorage.getItem('userId');
                const payload = {...params.data, author: userId};
                url = `${apiUrl}/${resource}`;
                options.method = 'POST';
                options.body = JSON.stringify(payload);
                break;
            case DELETE:
                url = `${apiUrl}/${resource}/${params.id}`;
                options.method = 'DELETE';
                break;
            default:
                throw new Error(`Unsupported fetch action type ${type}`);
        }
        return { url, options };
    };

    /**
     * @param {Object} response HTTP response from fetch()
     * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
     * @param {String} resource Name of the resource to fetch, e.g. 'posts'
     * @param {Object} params The data request params, depending on the type
     * @returns {Object} Data response
     */
    const convertHTTPResponse = (response, type, resource, params) => {
        const { headers, json } = response;
        switch (type) {
            case GET_MANY:
                if(resource === 'users') {
                    return { data: json.map(resource => ({ ...resource, id: resource._id })) }
                }
            case GET_MANY_REFERENCE:
            case GET_LIST:
                if (!headers.has('Content-Range')) {
                    throw new Error(
                        'The Content-Range header is missing in the HTTP Response. The simple REST data provider expects responses for lists of resources to contain this header with the total number of results to build the pagination. If you are using CORS, did you declare Content-Range in the Access-Control-Expose-Headers header?'
                    );
                }
                return {
                    data: json.map(resource => ({ ...resource, id: resource._id })),
                    total: parseInt(headers.get('Content-Range').split('/').pop(), 10),
                };
            case CREATE:
            case DELETE:
            case UPDATE:
                if(resource === 'phases') {
                    return { data: { ...params.data } };
                }
                return { data: { ...params.data, id: json._id } };
            default:
                return { data: { ...json, id: json._id } };
        }
    };

    /**
     * @param {string} type Request type, e.g GET_LIST
     * @param {string} resource Resource name, e.g. "posts"
     * @param {Object} payload Request parameters. Depends on the request type
     * @returns {Promise} the Promise for a data response
     */
    return (type, resource, params) => {
        // simple-rest doesn't handle filters on UPDATE route, so we fallback to calling UPDATE n times instead
        if (type === UPDATE_MANY) {
            return Promise.all(
                params.ids.map(id =>
                    httpClient(`${apiUrl}/${resource}/${id}`, {
                        method: 'PUT',
                        body: JSON.stringify(params.data),
                    })
                )
            ).then(responses => ({
                data: responses.map(response => response.json),
            }));
        }
        // simple-rest doesn't handle filters on DELETE route, so we fallback to calling DELETE n times instead
        if (type === DELETE_MANY) {
            return Promise.all(
                params.ids.map(id =>
                    httpClient(`${apiUrl}/${resource}/${id}`, {
                        method: 'DELETE',
                    })
                )
            ).then(responses => ({
                data: responses.map(response => response.json),
            }));
        }

        const { url, options } = convertDataRequestToHTTP(
            type,
            resource,
            params
        );
        return httpClient(url, options).then(response =>
            convertHTTPResponse(response, type, resource, params)
        );
    };
};
